import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { withStyles } from '@mui/styles'
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  IconButton,
  Collapse,
  Box,
} from '@mui/material'
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'

import { basicTableStyles } from '../basic-table/basic-table.styles'

const defaultKey = (i, row) => i

/**
 * @typedef {object} CollapsibleTableColumn
 * @property {string} key - Nombre del campo en el objeto de datos de la fila.
 * @property {'left' | 'center' | 'right'} [align='left'] - Alineación del contenido de la celda.
 * @property {string} label - Etiqueta que se muestra en el encabezado de la columna.
 * @property {'date' | 'datetime' | 'component' | 'responsable' | 'comboDocumentData' | 'comboData' | 'default'} [type] - Tipo de dato para formateo especial.
 * @property {React.ComponentType} [component] - Componente personalizado para renderizar el valor (cuando type='component').
 */

/**
 * @typedef {object} CollapsibleTableRow
 * @property {any[]} [fields] - Array de campos de detalle que se muestran en la fila expandible.
 * @property {any} - Cualquier otra propiedad correspondiente a los campos de las columnas.
 */

/**
 * @typedef {object} CollapsibleTableProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por withStyles.
 * @property {function(number, object): string | number} [rowKey] - Función para generar claves únicas para las filas.
 * @property {CollapsibleTableColumn[]} config - Array de configuraciones de columnas de la tabla.
 * @property {CollapsibleTableRow[]} data - Array de objetos con los datos de las filas.
 * @property {any} [context] - Contexto adicional pasado a componentes personalizados de columna.
 * @property {any} [getdata] - Prop recibida pero no utilizada actualmente.
 * @property {'small' | 'medium'} [size='medium'] - Tamaño de las celdas de la tabla.
 * @property {boolean} [showHeaders=true] - Si es true, muestra la fila de encabezados.
 * @property {function(object): React.ReactNode} [renderDetails] - Función personalizada para renderizar el contenido expandible de cada fila.
 * @property {function(object): void} [onClick] - Callback ejecutado al hacer clic en una fila. Si está presente, las filas son clickeables.
 * @property {boolean} [loading] - Si es true y hay onClick, muestra las filas en estado de carga.
 */

/**
 * Un componente de tabla con filas expandibles para mostrar detalles adicionales.
 *
 * Proporciona una tabla con:
 * - Filas que se pueden expandir/colapsar con un botón
 * - Contenido de detalle personalizable mediante renderDetails
 * - Formateo automático de fechas, componentes personalizados, y valores de combo
 * - Animación suave de expansión/colapso
 * - Solo muestra el botón de expansión si la fila tiene campos de detalle
 * - Soporte para filas clickeables con efectos hover
 *
 * @component
 * @example
 * const MyExpandableTable = () => {
 *   const config = [
 *     { key: 'nombre', label: 'Nombre', align: 'left' },
 *     { key: 'fecha', label: 'Fecha', type: 'date', align: 'center' },
 *     { key: 'estado', label: 'Estado', type: 'comboData' }
 *   ];
 *
 *   const data = [
 *     {
 *       nombre: 'Pedido 1',
 *       fecha: '2025-10-20',
 *       estado: 'completed',
 *       fields: [
 *         { label: 'Detalles', value: 'Información adicional del pedido' }
 *       ]
 *     }
 *   ];
 *
 *   const renderDetails = (row) => (
 *     <Box>
 *       <Typography variant="h6">Detalles del pedido</Typography>
 *       {row.fields.map((field, i) => (
 *         <Typography key={i}>{field.label}: {field.value}</Typography>
 *       ))}
 *     </Box>
 *   );
 *
 *   return (
 *     <CollapsibleTable
 *       config={config}
 *       data={data}
 *       renderDetails={renderDetails}
 *       size="small"
 *       showHeaders={true}
 *     />
 *   );
 * };
 *
 * @param {CollapsibleTableProps} props - Las props del componente CollapsibleTable.
 */
export const CollapsibleTable = withStyles(basicTableStyles)(({
  rowKey = defaultKey,
  classes,
  config,
  data,
  context,
  getdata,
  size = 'medium',
  showHeaders = true,
  renderDetails,
  ...props
}) => {
  const { formatDate, formatTime, formatMessage } = useIntl()
  const [openRows, setOpenRows] = useState({})

  const toggleRow = (index, hasDetails) => {
    if (hasDetails) {
      setOpenRows((prev) => ({ ...prev, [index]: !prev[index] }))
    }
  }

  const getParsedValue = (type, value, Component, context, row) => {
    switch (type) {
      case 'date':
        return (
          <Typography variant="body2" color="textSecondary">
            {formatDate(new Date(value))}
          </Typography>
        )
      case 'datetime':
        return (
          <Typography variant="body2" color="textSecondary">
            {formatDate(new Date(value)) + ' - ' + formatTime(new Date(value))}
          </Typography>
        )
      case 'component':
        return <Component value={value} context={context} row={row} />
      case 'responsable':
        const finalValue = row[value] ? `${row[value]} (${row['rol_usuario']})` : null
        return finalValue
      case 'comboDocumentData':
        return (
          <Typography variant="body2" color="textSecondary">
            {formatMessage({ id: `pages.supervision.edit.documents.${value}` })}
          </Typography>
        )
      case 'comboData':
        return (
          <Typography variant="body2" color="textSecondary">
            {formatMessage({ id: `global.basic_table.${value}` })}
          </Typography>
        )
      default:
        return value
    }
  }

  return (
    <TableContainer>
      <Table className={classes.table} aria-label="collapsible table" size={size}>
        {showHeaders && (
          <TableHead>
            <TableRow>
              <TableCell />
              {config.map(({ align = 'left', label }, i) => (
                <TableCell key={i} align={align}>
                  <Typography variant="subtitle1">{label}</Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
        )}
        <TableBody>
          {data.map((row, i) => {
            const hasDetails = row.fields && row.fields.length > 0

            return (
              <React.Fragment key={rowKey(i, row)}>
                <TableRow
                  className={
                    props.onClick
                      ? props.loading
                        ? classes.loadingRow
                        : classes.clickableRow
                      : classes.row
                  }
                  hover={!!props.onClick}
                  onClick={() => (props.onClick ? props.onClick(row) : null)}
                >
                  <TableCell>
                    {hasDetails && (
                      <IconButton size="small" onClick={() => toggleRow(i, hasDetails)}>
                        {openRows[i] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                      </IconButton>
                    )}
                  </TableCell>
                  {config.map(({ key, align = 'left', type, component }, j) => (
                    <TableCell key={j} align={align}>
                      {getParsedValue(type, row[key], component, context, row)}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell
                    style={{ paddingBottom: 0, paddingTop: 0 }}
                    colSpan={config.length + 1}
                  >
                    <Collapse in={openRows[i]} timeout="auto" unmountOnExit>
                      <Box margin={1}>
                        {renderDetails && hasDetails ? renderDetails(row) : null}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
})
